type NodeLookup = (id: string) => { type: string; parentId?: string } | undefined;

/** Only the artboard shared by the actual selection; no remembered or nearby fallback. */
export function selectionArtboard(selection: readonly string[], node: NodeLookup): string | null {
  let board: string | null = null;
  for (const id of selection) {
    let at: string | undefined = id;
    const visited = new Set<string>();
    let owner: string | null = null;
    while (at && !visited.has(at)) {
      visited.add(at);
      const current = node(at);
      if (current?.type === 'artboard') { owner = at; break; }
      at = current?.parentId;
    }
    if (!owner || (board !== null && owner !== board)) return null;
    board = owner;
  }
  return board;
}

/** Empty background clicks activate an outlined artboard without selecting an object.
 * Animate uses that same context, but an explicit loose or mixed selection takes priority.
 */
export function animationArtboard(selection: readonly string[], node: NodeLookup, active: string | null): string | null {
  if (selection.length > 0) return selectionArtboard(selection, node);
  return active && node(active)?.type === 'artboard' ? active : null;
}

/** The editing context follows deliberate choices, not camera movement. */
export class ActiveArtboard {
  private remembered: string | null = null;

  constructor(private readonly node: (id: string) => { type: string; parentId?: string } | undefined) {}

  choose(id: string): void {
    if (this.node(id)?.type === 'artboard') this.remembered = id;
  }

  resolve(selection: readonly string[], initial: () => string | null = () => null): string | null {
    for (const id of selection) {
      let at: string | undefined = id;
      const visited = new Set<string>();
      while (at && !visited.has(at)) {
        visited.add(at);
        const node = this.node(at);
        if (node?.type === 'artboard') return (this.remembered = at);
        at = node?.parentId;
      }
    }
    if (this.remembered && this.node(this.remembered)?.type === 'artboard') return this.remembered;
    const fallback = initial();
    this.remembered = fallback && this.node(fallback)?.type === 'artboard' ? fallback : null;
    return this.remembered;
  }
}
